import React, { useEffect, useMemo, useState } from 'react'
import { ConfigProvider, Menu, theme } from 'antd'
import { ItemType } from 'antd/es/menu/interface'
import { TFunction, useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { createGlobalStyle } from 'styled-components'
import { Route } from '../../routes/Route'
import { isSubPath } from './useNavigationMode'
import { isVisible, menuLabel } from './routeUtils'

interface SideNavProps {
  routes: Route[]
  collapsed?: boolean
  onNavigate?: () => void
}

const MENU_CLASS = 'side-nav'
const POPUP_CLASS = 'side-nav-popup'
/** Also given to the Menu tokens: the collapsed menu centres its icons with padding computed from this size. */
const ICON_SIZE = 16

// Global because collapsed submenus open their popups in a portal outside the menu.
const ActivePageStyle = createGlobalStyle<{ $barColor: string; $selectedBg: string }>`
  :is(.${MENU_CLASS}, .${POPUP_CLASS}) {
    /* Larger, bolder icons than the shared LucideIcon default; CSS overrides the svg's size/stroke attributes. */
    svg.ant-menu-item-icon {
      width: ${ICON_SIZE}px;
      height: ${ICON_SIZE}px;
      stroke-width: 2;
    }

    .ant-menu-item-selected,
    .ant-menu-submenu-selected > .ant-menu-submenu-title {
      font-weight: 600;
    }

    .ant-menu-item-selected,
    &.ant-menu-inline-collapsed .ant-menu-submenu-selected > .ant-menu-submenu-title {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        inset-block: 8px;
        width: 3px;
        border-radius: 2px;
        background: ${({ $barColor }) => $barColor};
      }
    }

    /* A collapsed menu only shows section icons, so mark the section containing the current page. */
    &.ant-menu-inline-collapsed .ant-menu-submenu-selected > .ant-menu-submenu-title {
      background: ${({ $selectedBg }) => $selectedBg};
    }
  }
`

const buildItems = (routes: Route[], t: TFunction): ItemType[] =>
  routes.filter(isVisible).map(route => {
    const label = menuLabel(route, t)
    const children = route.routes?.filter(isVisible) ?? []

    if (children.length > 0) {
      return {
        key: route.path,
        icon: route.icon,
        label,
        popupClassName: POPUP_CLASS,
        children: buildItems(children, t)
      }
    }
    return { key: route.path, icon: route.icon, label: <Link to={route.path}>{label}</Link> }
  })

/** Maps every visible menu path to the paths of its ancestor submenus. */
const collectAncestors = (routes: Route[], parents: string[] = [], result = new Map<string, string[]>()) => {
  routes.filter(isVisible).forEach(route => {
    result.set(route.path, parents)
    if (route.routes) collectAncestors(route.routes, [...parents, route.path], result)
  })
  return result
}

const SideNav: React.FC<SideNavProps> = ({ routes, collapsed, onNavigate }) => {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const {
    token: { colorBgContainer, colorPrimary, colorText }
  } = theme.useToken()

  const selectedBg = `color-mix(in srgb, ${colorPrimary} 14%, ${colorBgContainer})`

  const items = useMemo(() => buildItems(routes, t), [routes, t, i18n.language])
  const ancestors = useMemo(() => collectAncestors(routes), [routes])

  // Detail pages (e.g. /reporting/vouchers/123) highlight the closest menu item above them.
  const selectedKey = useMemo(
    () =>
      [...ancestors.keys()].filter(menuPath => isSubPath(pathname, menuPath)).sort((a, b) => b.length - a.length)[0],
    [ancestors, pathname]
  )

  const [openKeys, setOpenKeys] = useState<string[]>([])

  useEffect(() => {
    const selectedAncestors = (selectedKey && ancestors.get(selectedKey)) || []
    setOpenKeys(keys => Array.from(new Set([...keys, ...selectedAncestors])))
  }, [selectedKey, ancestors])

  return (
    <ConfigProvider
      theme={{
        components: {
          // A primary tint clearly stronger than the app frame's, so the current page stands out. The text stays
          // dark and the bar carries the primary colour.
          Menu: {
            itemSelectedBg: selectedBg,
            itemSelectedColor: colorText,
            subMenuItemSelectedColor: colorText,
            // Right padding; the left one comes from `inlineIndent`
            itemPaddingInline: 12,
            iconSize: ICON_SIZE,
            collapsedIconSize: ICON_SIZE
          }
        }
      }}
    >
      <ActivePageStyle $barColor={colorPrimary} $selectedBg={selectedBg} />
      <Menu
        className={MENU_CLASS}
        mode="inline"
        // Left padding of top-level items, and the extra indent of each nested level
        inlineIndent={16}
        items={items}
        selectedKeys={selectedKey ? [selectedKey] : []}
        // Inline-collapsed menus manage their own popup submenus, so only control them when expanded.
        {...(collapsed ? {} : { openKeys, onOpenChange: setOpenKeys })}
        onClick={onNavigate}
        style={{ borderInlineEnd: 'none', background: 'transparent' }}
      />
    </ConfigProvider>
  )
}

export default SideNav
